import React, { useState, useEffect } from 'react';
import {
  FunnelStage,
  StatusOption,
  StatusMapping,
  getUnmappedStatuses,
  mapMultipleStatusesToStage,
  removeStatusMapping,
  getFunnelStageById
} from '@/actions/data-fetch-funnel';
import {
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Tag
} from 'lucide-react';
import { toastMessage } from '@/utils/toast-message';

interface StatusMappingManagerProps {
  stage: FunnelStage;
  onClose: () => void;
}

const StatusMappingManager: React.FC<StatusMappingManagerProps> = ({
  stage,
  onClose
}) => {
  const [unmappedStatuses, setUnmappedStatuses] = useState<StatusOption[]>([]);
  const [currentMappings, setCurrentMappings] = useState<StatusMapping[]>(stage.statusMappings || []);
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingMappings, setIsAddingMappings] = useState(false);
  const [removingMappingId, setRemovingMappingId] = useState<number | null>(null);

  useEffect(() => {
    loadUnmappedStatuses();
    loadCurrentMappings();
  }, [stage.stage_id]);

  const loadUnmappedStatuses = async () => {
    try {
      setIsLoading(true);
      const response = await getUnmappedStatuses(false, stage.stage_id);
      if (response.success && response.data) {
        // Check if data is nested or direct array
        const statuses = Array.isArray(response.data) ? response.data : response.data.data;
        setUnmappedStatuses(statuses || []);
      }
    } catch (error) {
      console.error('Error loading unmapped statuses:', error);
      toastMessage('Failed to load available statuses', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCurrentMappings = async () => {
    try {
      const response = await getFunnelStageById(stage.stage_id);
      if (response.success && response.data?.data?.statusMappings) {
        setCurrentMappings(response.data.data.statusMappings);
      }
    } catch (error) {
      console.error('Error loading current mappings:', error);
    }
  };

  const handleStatusToggle = (statusId: number) => {
    setSelectedStatuses(prev =>
      prev.includes(statusId)
        ? prev.filter(id => id !== statusId)
        : [...prev, statusId]
    );
  };

  const handleAddMappings = async () => {
    if (selectedStatuses.length === 0) {
      toastMessage('Please select at least one status', 'warning');
      return;
    }

    try {
      setIsAddingMappings(true);
      const requestData = {
        stage_id_fk: stage.stage_id,
        status_ids: selectedStatuses,
        mapping_type: 'direct' as const
      };
      console.log('Sending mapping request:', requestData);
      const response = await mapMultipleStatusesToStage(requestData);

      if (response.success) {
        toastMessage('Status mappings added successfully', 'success');
        setSelectedStatuses([]);
        await loadUnmappedStatuses();
        await loadCurrentMappings();
      } else {
        toastMessage(response.message || 'Failed to add mappings', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to add mappings', 'error');
    } finally {
      setIsAddingMappings(false);
    }
  };

  // @ts-expect-error - statusName parameter reserved for future confirmation dialog
  const handleRemoveMapping = async (mappingId: number, statusName: string) => {
    try {
      setRemovingMappingId(mappingId);
      const response = await removeStatusMapping(mappingId);

      if (response.success) {
        toastMessage('Status mapping removed successfully', 'success');
        await loadUnmappedStatuses();
        await loadCurrentMappings();
      } else {
        toastMessage(response.message || 'Failed to remove mapping', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to remove mapping', 'error');
    } finally {
      setRemovingMappingId(null);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Current Mappings */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <CheckCircle2 className="w-5 h-5 mr-2 text-green-500" />
            Currently Mapped Statuses
          </h3>

          {currentMappings.length === 0 ? (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-2" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-400">
                    No statuses mapped yet
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-500 mt-1">
                    Add status mappings below to associate lead statuses with this funnel stage.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {currentMappings.map((mapping) => (
                <div
                  key={mapping.mapping_id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                        <Tag className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {mapping.status?.opt_name || 'Unknown Status'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Type: {mapping.mapping_type} • Priority: {mapping.priority}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveMapping(mapping.mapping_id, mapping.status?.opt_name || 'Unknown')}
                    disabled={removingMappingId === mapping.mapping_id}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {removingMappingId === mapping.mapping_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add New Mappings */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Plus className="w-5 h-5 mr-2 text-brand-500" />
            Add Status Mappings
          </h3>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
              <span className="ml-2 text-gray-500 dark:text-gray-400">Loading available statuses...</span>
            </div>
          ) : unmappedStatuses.length === 0 ? (
            <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle2 className="w-5 h-5 text-brand-600 dark:text-brand-400 mt-0.5 mr-2" />
                <div>
                  <p className="text-sm font-medium text-brand-800 dark:text-brand-400">
                    All statuses are mapped
                  </p>
                  <p className="text-sm text-brand-700 dark:text-brand-500 mt-1">
                    There are no more unmapped statuses available.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                {unmappedStatuses.map((status) => (
                  <label
                    key={status.opt_id}
                    className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status.opt_id)}
                      onChange={() => handleStatusToggle(status.opt_id)}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {status.opt_name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Value: {status.opt_value}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {selectedStatuses.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg">
                  <span className="text-sm font-medium text-brand-800 dark:text-brand-400">
                    {selectedStatuses.length} status{selectedStatuses.length !== 1 ? 'es' : ''} selected
                  </span>
                  <button
                    onClick={handleAddMappings}
                    disabled={isAddingMappings}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingMappings ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Mappings
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusMappingManager;
